import { useCallback, useEffect, useState } from 'react';

import { usePreviousValue } from '../../../../../../../common/hooks/usePreviousValue';
import { isNewValueAndSet } from '../../../../../../../utils/isNewValueAndSet';
import {
  DEFAULT_MODEL_VIEWER_ASPECT_RATIO_SELECTION,
  DEFAULT_MODEL_VIEWER_IMAGE_SIZE,
  MODEL_VIEWER_ASPECT_RATIO_AUTO,
  ModelViewerAspectRatioSelection,
  ModelViewerImageSizeValue,
  modelViewerAspectRatioValues
} from '../../modelViewerAspectRatioConstants';

/**
 * aspectRatio is sent verbatim as the aspect_ratio request param and shapes
 * the on-screen frame via CSS aspect-ratio. imageSize ("512" | "1K" | "2K" |
 * "4K") is sent verbatim as the image_size request param — the backend
 * resolves the output resolution from the aspect_ratio + image_size pair.
 * requested_width/requested_height are still measured from the (ratio-shaped)
 * viewer box at request time in the generation hooks, for backward
 * compatibility only.
 *
 * The "auto" selection is UI-only: the frame stays unconstrained (no
 * letterboxing, no visible crop) and each consumer resolves it to the nearest
 * preset at request time via resolveModelViewerAspectRatio, so requests and
 * screenshots behave exactly as if auto did not exist.
 */
function useModelViewerAspectRatio() {
  const [aspectRatio, setAspectRatio] =
    useState<ModelViewerAspectRatioSelection>(
      DEFAULT_MODEL_VIEWER_ASPECT_RATIO_SELECTION
    );
  const [imageSize, setImageSize] = useState<ModelViewerImageSizeValue>(
    DEFAULT_MODEL_VIEWER_IMAGE_SIZE
  );
  const [showGridLines, setShowGridLines] = useState(false);

  // undefined in auto mode — the frame then fills the available box instead of
  // being constrained by CSS aspect-ratio.
  const getAspectRatioCssValue = useCallback((): string | undefined => {
    if (aspectRatio === MODEL_VIEWER_ASPECT_RATIO_AUTO) return undefined;
    const preset = modelViewerAspectRatioValues[aspectRatio];
    return `${preset.width} / ${preset.height}`;
  }, [aspectRatio]);

  const toggleGridLines = useCallback(() => setShowGridLines((v) => !v), []);

  const prevAspectRatio = usePreviousValue(aspectRatio);

  useEffect(() => {
    if (isNewValueAndSet(prevAspectRatio, aspectRatio)) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [aspectRatio, prevAspectRatio]);

  return {
    aspectRatio,
    setAspectRatio,
    imageSize,
    setImageSize,
    getAspectRatioCssValue,
    showGridLines,
    toggleGridLines
  };
}

export default useModelViewerAspectRatio;
